import { call } from "@saas/api-client";
import { Badge, Button, Card, ConfirmAction, DescriptionList, ErrorAlert, formatDate, Loading, PageHeader, Table, useAsync, useCommand } from "@saas/ui";
import { Link, useNavigate, useParams } from "react-router";
import { api } from "./api";
import { StatusBadge } from "./TenantsPage";

export function TenantDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const tenant = useAsync(() => call(api.GET("/api/tenants/{id}", { params: { path: { id } } })), [id]);
  const members = useAsync(() => call(api.GET("/api/tenants/{tenantId}/members", { params: { path: { tenantId: id } } })), [id]);
  const command = useCommand();

  if (tenant.loading && !tenant.data) return <Loading />;
  if (tenant.error || !tenant.data) return <ErrorAlert error={tenant.error ?? new Error("Tenant not found.")} />;
  const t = tenant.data;
  const suspended = t.status === "Suspended";

  const act = async (action: "suspend" | "reactivate") => {
    const path = action === "suspend" ? "/api/tenants/{id}/suspend" : "/api/tenants/{id}/reactivate";
    if (await command.run(() => call(api.POST(path, { params: { path: { id } } })))) tenant.reload();
  };

  const remove = async () => {
    if (await command.run(() => call(api.DELETE("/api/tenants/{id}", { params: { path: { id } } })))) navigate("/");
  };

  return (
    <>
      <p>
        <Link to="/">← All tenants</Link>
      </p>
      <PageHeader
        title={t.name}
        description={`Tenant ${t.slug}`}
        actions={
          <div className="sb-actions">
            {suspended ? (
              <Button variant="primary" onClick={() => act("reactivate")} disabled={command.pending}>
                Reactivate
              </Button>
            ) : (
              <Button variant="danger" onClick={() => act("suspend")} disabled={command.pending}>
                Suspend
              </Button>
            )}
            {suspended && (
              <ConfirmAction
                label="Delete…"
                confirmLabel={`Delete ${t.name} permanently`}
                dismissLabel="Keep tenant"
                message={<p>Deleting removes all of {t.name}'s data and its users in the identity provider. This cannot be undone.</p>}
                onConfirm={remove}
                pending={command.pending}
              />
            )}
          </div>
        }
      />
      <ErrorAlert error={command.error} />
      <div className="sb-grid">
        <Card title="Details">
          <DescriptionList
            items={[
              ["Status", <StatusBadge key="s" status={t.status} />],
              ["Display name", t.displayName],
              ["First TenantAdmin", t.adminEmail],
              ["Support email", t.supportEmail ?? "—"],
              ["Created", formatDate(t.createdAt)],
              ["Suspended", formatDate(t.suspendedAt)],
              [
                "Identity provider",
                t.identityProvisionedAt
                  ? `Provisioned ${formatDate(t.identityProvisionedAt)}`
                  : t.adminEmailInUseAt
                    ? "Not provisioned: the TenantAdmin email belongs to another tenant. Suspend and delete this tenant, then create it again with another email."
                    : "Pending",
              ],
            ]}
          />
        </Card>
      </div>
      <Card title="Members" description="Owner view across tenants. Every access is recorded in the audit log.">
        <ErrorAlert error={members.error} />
        {members.data && (
          <Table caption={`${members.data.length} member(s)`} headers={["Email", "Role", "Status", "Last sign-in"]}>
            {members.data.map((m) => (
              <tr key={m.id}>
                <td>{m.email}</td>
                <td>{m.role}</td>
                <td>
                  <Badge tone={m.status === "Active" ? "success" : "neutral"}>{m.status}</Badge>
                </td>
                <td>{formatDate(m.lastSignInAt)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}
